import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { LockOutlined } from "@ant-design/icons";
import { Body } from "../../../components/main-body/body";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import useNotification from "../../../components/notification/notification";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import ApiRoles from "../../../api/roles";
import RolesTable from "./roles-table";
import RoleCard from "./role-card";
import RoleCreate from "./role-create";
import { filterRoles, SITE_SCOPING_OPTIONS, TYPE_OPTIONS } from "./roles-filter";
import "./styles.css";

// The line of organization roles above the table: one line plus its margin.
const LOCKED_HEIGHT = 38;

/**
 * LF360-2043 / 2044 / 2045 — Administration › Organization settings › Roles
 * and permissions: the organization's project roles, searchable and
 * filterable, and one role's details. Organization and System admins only.
 *
 * LF360-2046 / 2049: custom roles are created here and deleted from their card.
 */
const RolesManagement = () => {
  const { id } = useParams();
  const navigate = useProjectNavigate();
  const { openNotification } = useNotification();

  const [roles, setRoles] = useState([]);
  const [organizationRoles, setOrganizationRoles] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [details, setDetails] = useState({ role: null, projects: [] });
  const [customRoleLimit, setCustomRoleLimit] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const loadRoles = useCallback(() => ApiRoles.getRoles().then(({ data }) => {
    setRoles(data?.roles || []);
    setOrganizationRoles(data?.organizationRoles || []);
    setCustomRoleLimit(data?.customRoleLimit ?? null);
  }), []);

  useEffect(() => {
    Promise.all([loadRoles(), ApiRoles.getCatalog()])
      .then(([, catalogResponse]) => setCatalog(catalogResponse.data || null))
      .catch(() => openNotification({ type: "error", message: <p>Unable to load roles</p> }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) {
      setDetails({ role: null, projects: [] });
      return;
    }
    ApiRoles.getRole(id)
      .then(({ data }) => setDetails({ role: data?.role || null, projects: data?.projects || [] }))
      .catch(() => openNotification({ type: "error", message: <p>Unable to load the role</p> }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const shown = useMemo(() => filterRoles(roles, { search, filters }), [roles, search, filters]);

  const permissionOptions = useMemo(
    () => (catalog?.permissions || []).map((p) => ({ id: p.key, name: p.label })),
    [catalog]
  );

  const updateFilters = useCallback((value) => setFilters({ ...value }), []);

  // LF360-2048: restoring all built-in roles sends the versions this list shows.
  const builtInVersions = useMemo(
    () => Object.fromEntries(roles.filter((r) => r.type === "builtIn").map((r) => [r.id, r.version])),
    [roles]
  );

  // A saved or restored role replaces its row and, when open, its card.
  const applyChanged = useCallback((changed) => {
    const byId = new Map(changed.map((role) => [role.id, role]));
    setRoles((current) => current.map((role) => byId.get(role.id) || role));
    setDetails((current) => (
      current.role && byId.has(current.role.id)
        ? { ...current, role: byId.get(current.role.id) }
        : current
    ));
  }, []);

  // LF360-2046: off, with the reason, once custom roles reach the limit.
  const customCount = roles.filter((role) => role.type === "custom").length;
  const atLimit = customRoleLimit !== null && customCount >= customRoleLimit;

  const headerProps = {
    title: "Roles and permissions",
    search: { value: search, onHandleChange: setSearch },
    apply: [{
      key: "create-role",
      testID: "role-create",
      label: "Create role",
      onClick: () => setIsCreating(true),
      disabled: atLimit,
      tooltip: atLimit ? `Limit of ${customRoleLimit} custom roles reached` : undefined,
    }],
  };

  // LF360-2046 / 2049: a new role opens in its card; a deleted one closes it.
  // Members counts move with a delete, so the list is read again.
  const handleCreated = (role) => {
    setIsCreating(false);
    loadRoles();
    navigate(`/administration/roles/${role.id}`);
  };

  const handleDeleted = () => {
    loadRoles();
    navigate("/administration/roles");
  };

  // Above the table, not inside its block: the table's header is the block's
  // first row, as on every other list.
  const locked = (
    <div className="roles-locked">
      {organizationRoles.map((role) => (
        <span key={role.key}>
          <LockOutlined /> {role.name}: full access
        </span>
      ))}
    </div>
  );

  return (
    <Body
      header={headerProps}
      filters={
        <DropdownFilters
          filtersBy={[
            { name: "Type", id: "type", children: TYPE_OPTIONS },
            { name: "Site scoping", id: "siteScoping", children: SITE_SCOPING_OPTIONS },
            { name: "Permission", id: "permission", children: permissionOptions },
          ]}
          updateFilterValue={updateFilters}
        />
      }
    >
      {locked}
      <RolesTable
        data={shown}
        heightOffset={LOCKED_HEIGHT}
        onHandleRowClick={(role) => navigate(`/administration/roles/${role.id}`)}
      />
      <RoleCard
        role={details.role}
        projects={details.projects}
        catalog={catalog}
        isOpen={Boolean(id)}
        close={() => navigate("/administration/roles")}
        roles={roles}
        builtInVersions={builtInVersions}
        onChanged={applyChanged}
        onDeleted={handleDeleted}
      />
      <RoleCreate
        isOpen={isCreating}
        close={() => setIsCreating(false)}
        catalog={catalog}
        roles={roles}
        onCreated={handleCreated}
      />
    </Body>
  );
};

export default RolesManagement;
